import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/axios';
import useProjectStore from '../stores/projectStore';

export function useAnalysisClusters(filters = {}) {
  const currentProjectId = useProjectStore((s) => s.currentProjectId);
  return useQuery({
    queryKey: ['analysis-clusters', currentProjectId, filters],
    queryFn: async () => {
      const { data } = await api.get('/analysis-clusters', { params: filters });
      return data.clusters;
    },
  });
}

export function useAnalysisCluster(id) {
  return useQuery({
    queryKey: ['analysis-clusters', id],
    queryFn: async () => {
      const { data } = await api.get(`/analysis-clusters/${id}`);
      return data;
    },
    enabled: !!id,
    refetchInterval: (data) => {
      if (!data) return false;
      if (data.status === 'synthesizing') return 3000;
      // Sub-Analysen noch in Arbeit → weiter pollen
      const subPending = (data.analyses || []).some(a => ['pending', 'processing'].includes(a.status));
      return subPending ? 4000 : false;
    },
  });
}

export function useCreateCluster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post('/analysis-clusters', payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['analysis-clusters'] });
    },
  });
}

export function useUpdateCluster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const { data } = await api.patch(`/analysis-clusters/${id}`, payload);
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['analysis-clusters', vars.id] });
      qc.invalidateQueries({ queryKey: ['analysis-clusters'] });
    },
  });
}

export function useDeleteCluster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(`/analysis-clusters/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['analysis-clusters'] }),
  });
}

export function useAttachClusterItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, analysis_ids }) => {
      const { data } = await api.post(`/analysis-clusters/${id}/items`, { analysis_ids });
      return data;
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['analysis-clusters', vars.id] }),
  });
}

export function useDetachClusterItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, itemId }) => {
      await api.delete(`/analysis-clusters/${id}/items/${itemId}`);
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['analysis-clusters', vars.id] }),
  });
}

export function useSynthesizeCluster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await api.post(`/analysis-clusters/${id}/synthesize`);
      return data;
    },
    // Warten bis der Refetch durch ist, damit isPending bis zum Status-Flip
    // auf "synthesizing" true bleibt (kein Flackern des Loading-Banners).
    onSuccess: async (_d, id) => {
      await qc.refetchQueries({ queryKey: ['analysis-clusters', id] });
    },
  });
}

export function useUpdateClusterShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const { data } = await api.patch(`/analysis-clusters/${id}/share`, payload);
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['analysis-clusters', vars.id] });
      qc.invalidateQueries({ queryKey: ['analysis-clusters'] });
    },
  });
}
